import { useEffect } from "react";
import LocationDao from "@dao/LocationDao";
import UserDao from "@dao/UserDao";
import { useGlobalContext } from "@components/contextProvider/ContextProvider";
import type { LocationDto, SchoolYear } from "@/types/GlobalState";

interface LocationSearchPayload {
    results?: LocationDto[];
    [key: string]: unknown;
}

let getSY = true;
let getAllSY = true;

/**
 * Custom React hook to fetch all location DTOs and populate global context.
 */
export const useSetAllLocationDtos = (enabled = true): void => {
    const { dispatch, state } = useGlobalContext();
    const { allLocationDtos, token } = state || {};

    useEffect(() => {
        if (enabled && token && !allLocationDtos && getAllSY) {
            getAllSY = false;
            const options = {
                action: "locationsSearchableRead" as const,
                params: {
                    facetField: "locationType",
                    searchString:
                        "locationType=ELEMENTARY_SCHOOL&locationType=MIDDLE_SCHOOL&locationType=HIGH_SCHOOL&locationType=ALTERNATIVE_SCHOOL&locationType=CHARTER_SCHOOL",
                    sort: "name",
                    numRows: "1000"
                }
            };

            const fetchLocations = async (): Promise<void> => {
                try {
                    const response = await LocationDao<{ payload?: LocationSearchPayload }>(options);
                    if (response?.data) {
                        const payload = response.data.payload;
                        if (payload?.results && payload.results.length > 0) {
                            const filterOutNoIcId = payload.results.filter(
                                (obj) =>
                                    obj.icId !== undefined &&
                                    String(obj.icId).length > 0 &&
                                    (Array.isArray(obj.grade) ? obj.grade.length > 0 : Boolean(obj.grade))
                            );
                            if (filterOutNoIcId && filterOutNoIcId.length > 0) {
                                dispatch({
                                    type: "AllLocationDtos",
                                    allLocationDtos: filterOutNoIcId
                                });
                            }
                        }
                    }
                } catch (error) {
                    console.error("ContextSetter: Error fetching location DTOs:", error);
                } finally {
                    getAllSY = true;
                }
            };
            void fetchLocations();
        }
    }, [enabled, token, allLocationDtos, dispatch]);
};

/**
 * Custom React hook to fetch active school year DTO and populate global context.
 */
export const useSetSchoolYearDto = (): void => {
    const { dispatch, state } = useGlobalContext();
    const { schoolYearDto, token } = state || {};

    useEffect(() => {
        if (token && !schoolYearDto && getSY) {
            getSY = false;
            const fetchSchoolYear = async (): Promise<void> => {
                try {
                    const response = await UserDao<{ payload?: SchoolYear }>({
                        action: "activeSchoolYearRead"
                    });
                    if (response?.data) {
                        const payload = response.data.payload;
                        if (payload && typeof payload === "object" && Object.keys(payload).length > 0) {
                            dispatch({
                                type: "SchoolYearDto",
                                schoolYearDto: payload
                            });
                        }
                    }
                } catch (error) {
                    console.error("ContextSetter: Error fetching active school year:", error);
                } finally {
                    getSY = true;
                }
            };
            void fetchSchoolYear();
        }
    }, [token, schoolYearDto, dispatch]);
};

/**
 * Helper hook to set both location and school year context data.
 */
export const useSetContextData = (options?: { includeLocations?: boolean }): void => {
    useSetSchoolYearDto();
    useSetAllLocationDtos(options?.includeLocations ?? true);
};

/**
 * Legacy aliases for backwards compatibility with legacy imports
 */
export const SetAllLocationDtos = useSetAllLocationDtos;
export const SetSchoolYearDto = useSetSchoolYearDto;
